// @ts-nocheck
/**
 * SystemMonitor - 系统监控组件
 *
 * 功能：
 * - 系统状态监控
 * - 审计日志查看
 * - 缓存管理
 */

import React, { useState, useEffect } from 'react';
import { apiGet, apiPost } from '../../utils/apiClient';

interface SystemMonitorProps {
  onRefresh?: () => void;
}

interface Filters {
  resource_type: string;
  action: string;
}

export default function SystemMonitor({ onRefresh }: SystemMonitorProps) {
  const [systemStatus, setSystemStatus] = useState<any>(null);
  const [auditLogs, setAuditLogs] = useState<any[]>([]);
  const [auditStats, setAuditStats] = useState<any>(null);
  const [dailyActivity, setDailyActivity] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [logsLoading, setLogsLoading] = useState<boolean>(false);
  const [page, setPage] = useState<number>(1);
  const [totalLogs, setTotalLogs] = useState<number>(0);
  const [filters, setFilters] = useState<Filters>({
    resource_type: '',
    action: ''
  });

  useEffect(() => {
    fetchSystemStatus();
    fetchAuditStats();
    fetchAuditLogs();
  }, []);

  useEffect(() => {
    fetchAuditLogs();
  }, [page, filters]);

  const fetchSystemStatus = async () => {
    try {
      const response = await apiGet('/api/admin/config/system/status') as any;
      if (response.success) {
        setSystemStatus(response.status);
      }
    } catch (error: any) {
      console.error('获取系统状态失败:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAuditStats = async () => {
    try {
      const response = await apiGet('/api/admin/config/audit-logs/statistics?days=30') as any;
      if (response.success) {
        setAuditStats(response.statistics);
        setDailyActivity(response.daily_activity || []);
      }
    } catch (error: any) {
      console.error('获取审计统计失败:', error);
    }
  };

  const fetchAuditLogs = async () => {
    try {
      setLogsLoading(true);
      const params = new URLSearchParams({
        page: page.toString(),
        page_size: '20'
      });

      if (filters.resource_type) params.append('resource_type', filters.resource_type);
      if (filters.action) params.append('action', filters.action);

      const response = await apiGet(`/api/admin/config/audit-logs?${params}`) as any;
      if (response.success) {
        setAuditLogs(response.logs);
        setTotalLogs(response.total);
      }
    } catch (error: any) {
      console.error('获取审计日志失败:', error);
    } finally {
      setLogsLoading(false);
    }
  };

  const handleClearCache = async (cacheType: string | null = null) => {
    const msg = cacheType
      ? `确定清除 ${cacheType} 类型的缓存?`
      : '确定清除所有配置缓存?';

    if (!window.confirm(msg)) return;

    try {
      const url = cacheType
        ? `/api/admin/config/cache/clear?cache_type=${cacheType}`
        : '/api/admin/config/cache/clear';

      const response = await apiPost(url) as any;
      if (response.success) {
        alert(response.message);
        onRefresh?.();
      } else {
        alert('清除失败: ' + response.message);
      }
    } catch (error: any) {
      console.error('清除缓存失败:', error);
      alert('清除失败');
    }
  };

  const getActionColor = (action: string): string => {
    const colors: Record<string, string> = {
      create: '#10b981',
      update: '#00bbf9',
      delete: '#ef4444',
      rollback: '#fbbf24',
      default: '#9ca3af'
    };
    return colors[action] || colors.default;
  };

  const getResourceIcon = (resourceType: string): string => {
    const icons: Record<string, string> = {
      prompt: '📝',
      api_config: '🔌',
      voice: '🎙️',
      user: '👤',
      setting: '⚙️',
      cache: '🗄️',
      default: '📄'
    };
    return icons[resourceType] || icons.default;
  };

  const formatDate = (dateStr: string): string => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('zh-CN', {
      month: 'numeric',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return <div className="loading-spinner"></div>;
  }

  return (
    <div className="system-monitor">
      {/* 系统状态 */}
      <div className="admin-panel">
        <div className="panel-header">
          <h3 className="panel-title">📊 系统状态</h3>
          <button className="btn-secondary" onClick={fetchSystemStatus}>
            刷新状态
          </button>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
          {/* 数据库状态 */}
          <div className="status-card">
            <div className="status-card-icon">🗄️</div>
            <div className="status-card-content">
              <div className="status-card-title">数据库</div>
              <div className={`status-badge ${systemStatus?.database?.status === 'healthy' ? 'badge-success' : 'badge-danger'}`}>
                {systemStatus?.database?.status === 'healthy' ? '正常' : '异常'}
              </div>
            </div>
          </div>

          {/* Redis状态 */}
          <div className="status-card">
            <div className="status-card-icon">⚡</div>
            <div className="status-card-content">
              <div className="status-card-title">Redis 缓存</div>
              <div className={`status-badge ${systemStatus?.redis?.status === 'healthy' ? 'badge-success' : 'badge-danger'}`}>
                {systemStatus?.redis?.status === 'healthy' ? '正常' : '异常'}
              </div>
              {systemStatus?.redis?.used_memory_human && (
                <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.5)', marginTop: '4px' }}>
                  内存: {systemStatus.redis.used_memory_human}
                </div>
              )}
            </div>
          </div>

          {/* 配置统计 */}
          {systemStatus?.config_counts && (
            <>
              <div className="status-card">
                <div className="status-card-icon">📝</div>
                <div className="status-card-content">
                  <div className="status-card-title">提示词配置</div>
                  <div style={{ fontSize: '24px', fontWeight: '600', color: '#00f5d4' }}>
                    {systemStatus.config_counts.prompts}
                  </div>
                </div>
              </div>

              <div className="status-card">
                <div className="status-card-icon">🔌</div>
                <div className="status-card-content">
                  <div className="status-card-title">API 配置</div>
                  <div style={{ fontSize: '24px', fontWeight: '600', color: '#00f5d4' }}>
                    {systemStatus.config_counts.api_configs}
                  </div>
                </div>
              </div>

              <div className="status-card">
                <div className="status-card-icon">🎙️</div>
                <div className="status-card-content">
                  <div className="status-card-title">音色配置</div>
                  <div style={{ fontSize: '24px', fontWeight: '600', color: '#00f5d4' }}>
                    {systemStatus.config_counts.voices}
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* 缓存管理 */}
      <div className="admin-panel">
        <div className="panel-header">
          <h3 className="panel-title">🗄️ 缓存管理</h3>
        </div>

        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
          <button className="btn-secondary" onClick={() => handleClearCache('prompt')}>
            清除提示词缓存
          </button>
          <button className="btn-secondary" onClick={() => handleClearCache('api')}>
            清除API缓存
          </button>
          <button className="btn-secondary" onClick={() => handleClearCache('voice')}>
            清除音色缓存
          </button>
          <button className="btn-danger" onClick={() => handleClearCache()}>
            清除所有缓存
          </button>
        </div>
      </div>

      {/* 审计统计 */}
      {auditStats && (
        <div className="admin-panel">
          <div className="panel-header">
            <h3 className="panel-title">📈 审计统计 (近30天)</h3>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '12px' }}>
            <div className="stat-box">
              <div className="stat-box-value">{auditStats.total_operations}</div>
              <div className="stat-box-label">总操作数</div>
            </div>
            <div className="stat-box">
              <div className="stat-box-value">{auditStats.active_admins}</div>
              <div className="stat-box-label">活跃管理员</div>
            </div>
            <div className="stat-box">
              <div className="stat-box-value" style={{ color: '#10b981' }}>{auditStats.by_action?.create || 0}</div>
              <div className="stat-box-label">创建</div>
            </div>
            <div className="stat-box">
              <div className="stat-box-value" style={{ color: '#00bbf9' }}>{auditStats.by_action?.update || 0}</div>
              <div className="stat-box-label">更新</div>
            </div>
            <div className="stat-box">
              <div className="stat-box-value" style={{ color: '#ef4444' }}>{auditStats.by_action?.delete || 0}</div>
              <div className="stat-box-label">删除</div>
            </div>
            <div className="stat-box">
              <div className="stat-box-value" style={{ color: '#fbbf24' }}>{auditStats.by_action?.rollback || 0}</div>
              <div className="stat-box-label">回滚</div>
            </div>
          </div>
        </div>
      )}

      {/* 审计日志 */}
      <div className="admin-panel">
        <div className="panel-header">
          <h3 className="panel-title">📋 审计日志</h3>

          {/* 筛选器 */}
          <div style={{ display: 'flex', gap: '12px' }}>
            <select
              className="form-select"
              style={{ width: '120px' }}
              value={filters.resource_type}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                setFilters({ ...filters, resource_type: e.target.value });
                setPage(1);
              }}
            >
              <option value="">全部类型</option>
              <option value="prompt">提示词</option>
              <option value="api_config">API</option>
              <option value="voice">音色</option>
              <option value="user">用户</option>
              <option value="setting">设置</option>
            </select>

            <select
              className="form-select"
              style={{ width: '120px' }}
              value={filters.action}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                setFilters({ ...filters, action: e.target.value });
                setPage(1);
              }}
            >
              <option value="">全部操作</option>
              <option value="create">创建</option>
              <option value="update">更新</option>
              <option value="delete">删除</option>
              <option value="rollback">回滚</option>
            </select>
          </div>
        </div>

        {logsLoading ? (
          <div className="loading-spinner"></div>
        ) : (
          <>
            <table className="admin-table">
              <thead>
                <tr>
                  <th>时间</th>
                  <th>操作者</th>
                  <th>操作</th>
                  <th>资源</th>
                  <th>详情</th>
                  <th>IP</th>
                </tr>
              </thead>
              <tbody>
                {auditLogs.map((log: any) => (
                  <tr key={log.id}>
                    <td style={{ whiteSpace: 'nowrap' }}>{formatDate(log.created_at)}</td>
                    <td>{log.username}</td>
                    <td>
                      <span
                        className="badge"
                        style={{ background: `${getActionColor(log.action)}20`, color: getActionColor(log.action) }}
                      >
                        {log.action}
                      </span>
                    </td>
                    <td>
                      <span style={{ marginRight: '4px' }}>{getResourceIcon(log.resource_type)}</span>
                      {log.resource_name || log.resource_id || '-'}
                    </td>
                    <td style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {log.new_value ? JSON.stringify(log.new_value).substring(0, 50) + '...' : '-'}
                    </td>
                    <td style={{ fontSize: '12px', color: 'rgba(255,255,255,0.5)' }}>{log.ip_address || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            {auditLogs.length === 0 && (
              <div className="empty-state">
                <div className="empty-state-icon">📋</div>
                <div className="empty-state-text">暂无审计日志</div>
              </div>
            )}

            {/* 分页 */}
            {totalLogs > 20 && (
              <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginTop: '16px' }}>
                <button
                  className="btn-secondary"
                  disabled={page <= 1}
                  onClick={() => setPage(p => p - 1)}
                >
                  上一页
                </button>
                <span style={{ padding: '8px 16px', color: 'rgba(255,255,255,0.6)' }}>
                  {page} / {Math.ceil(totalLogs / 20)}
                </span>
                <button
                  className="btn-secondary"
                  disabled={page >= Math.ceil(totalLogs / 20)}
                  onClick={() => setPage(p => p + 1)}
                >
                  下一页
                </button>
              </div>
            )}
          </>
        )}
      </div>

      <style>{`
        .status-card {
          display: flex;
          align-items: center;
          gap: 16px;
          padding: 16px;
          background: rgba(255,255,255,0.03);
          border: 1px solid rgba(255,255,255,0.08);
          border-radius: 10px;
        }
        .status-card-icon {
          font-size: 28px;
        }
        .status-card-content {
          flex: 1;
        }
        .status-card-title {
          font-size: 13px;
          color: rgba(255,255,255,0.6);
          margin-bottom: 4px;
        }
        .stat-box {
          padding: 16px;
          background: rgba(0,0,0,0.2);
          border-radius: 8px;
          text-align: center;
        }
        .stat-box-value {
          font-size: 28px;
          font-weight: 600;
          color: #00f5d4;
        }
        .stat-box-label {
          font-size: 12px;
          color: rgba(255,255,255,0.5);
          margin-top: 4px;
        }
      `}</style>
    </div>
  );
}
